import {} from 'react';
import { Route, Redirect, useLocation } from 'react-router-dom';
import {
  isAuthenticated,
  checkRole,
  userProfile,
  getDefaultNavigationLink,
  normalizeRoles,
} from 'utils/auth';
import { getServerProfile } from 'utils/serverProfileCache';
import { useSelector } from 'react-redux';
import { NotAuthorizedPage } from 'app/components/NotAuthorizedPage/Loadable';
import { selectDrawerOpen } from 'app/pages/Header/selectors';
import { Header } from 'app/pages/Header/Loadable';
import { Sidebar } from 'app/pages/Sidebar/Loadable';
import { ComponentPageContainer } from 'app/components/StyledComponents';
import { Box } from '@mui/material';
import { shouldRestrictToAILayout } from './AIAvatarRoute';
import { AIAvatarPageShell } from 'app/NewAIAvatarLayout/sharedComponents/AIAvatarPageShell';
import { isAutopilotPath } from 'app/pages/AutopilotPage/utils/autopilotPaths';
import { isImpactHubPath } from 'app/pages/ImpactHubPage/utils/viewConfig';

export const AuthorizedRoute = ({ component, roles, ...rest }: any) => {
  const isDrawerOpen = useSelector(selectDrawerOpen) as boolean;
  const user = userProfile();
  const location = useLocation();
  // const hasAccess = roles ? checkRole(user?.roles || [], roles) : true;
  const hasAccess = true;

  if (!user) {
    return <Redirect to="/login" />;
  }

  // Check old_layout flag from user token
  // If old_layout is false, user should use NewAIAvatarLayout
  const oldLayout = user?.old_layout !== undefined ? user.old_layout : true; // Default to true if not present
  if (oldLayout === false) {
    // Shared routes (e.g. /autopilot, /impact-hub) keep the same URL in both themes —
    // render NewAIAvatarLayout chrome without remapping under /ai-avatar-layout.
    if (
      isAutopilotPath(location.pathname) ||
      isAutopilotPath(rest.path) ||
      isImpactHubPath(location.pathname) ||
      isImpactHubPath(rest.path)
    ) {
      return (
        <AIAvatarPageShell>
          <Route component={component} {...rest} />
        </AIAvatarPageShell>
      );
    }

    // User should use layout route namespace (derived from default nav link).
    // Preserve current path/query by mapping legacy paths under active layout base.
    const defaultNavLink = getDefaultNavigationLink();
    const fallbackPath = '/ai-avatar-layout';
    const preferredPath = defaultNavLink || fallbackPath;
    const [pathWithoutQuery] = preferredPath.split('?');
    const pathSegments = pathWithoutQuery.split('/').filter(Boolean);
    const layoutBase = pathSegments.length > 0 ? `/${pathSegments[0]}` : fallbackPath;

    if (location.pathname && !location.pathname.startsWith(layoutBase)) {
      const remappedPath = `${layoutBase}${location.pathname}${location.search || ''}`;
      return <Redirect to={remappedPath} />;
    }

    const redirectPath = preferredPath || fallbackPath;
    return <Redirect to={redirectPath} />;
  }

  // Check if user should be restricted to AI Avatar Layout only (legacy role-based check)
  const userRoles = normalizeRoles(user?.roles);
  const restrictedToAILayout = shouldRestrictToAILayout(userRoles);
  
  // If user is NewAIAvatarLayout role and trying to access other routes, redirect to AI layout
  // Allow shared Autopilot / Impact Hub paths without remapping.
  if (
    restrictedToAILayout &&
    !rest.path?.startsWith('/ai-avatar-layout') &&
    !isAutopilotPath(rest.path) &&
    !isImpactHubPath(rest.path)
  ) {
    return <Redirect to="/ai-avatar-layout" />;
  }

  if (!hasAccess) {
    return (
      <>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>

        <Header />
        <Box sx={{display: 'flex', flexDirection: 'row', flex: 1}}>
          <Sidebar />
          <ComponentPageContainer
            style={{ 
              marginLeft: '64px',
              transition: 'margin-left 0.2s ease-in-out'
            }}
          >
            <Route component={NotAuthorizedPage} {...rest} />
          </ComponentPageContainer>
        </Box>
      </Box>
      </>
    );
  } else
    return (
      <>
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
          <Header />
          <Box sx={{display: 'flex', flexDirection: 'row', width: '100%'}}>
            <Sidebar />
            <ComponentPageContainer
              style={{ 
                marginLeft: '64px',
                transition: 'margin-left 0.2s ease-in-out',
                width: 'calc(100% - 64px)'
              }}
            >
              <Route component={component} {...rest} />
            </ComponentPageContainer>
          </Box>
        </Box>
      </>
    );
};

export const UnAuthorizedRoute = ({ component, ...rest }: any) => {
  // if (!isAuthenticated()) {
  return <Route component={component} {...rest} />;
  // }
  return <Redirect push to="/" />;
};

// New FlexibleRoute component that works with or without authentication
export const FlexibleRoute = ({ component, roles, ...rest }: any) => {
  const isDrawerOpen = useSelector(selectDrawerOpen) as boolean;
  const location = useLocation();
  const user = userProfile(false); // Don't redirect automatically
  const isLoggedIn = isAuthenticated();
  const serverProfile = getServerProfile() as any;
  const serverOldLayout =
    typeof serverProfile?.old_layout === 'boolean'
      ? serverProfile.old_layout
      : null;
  
  // Shareable Nurturing History: skip blue Header/Sidebar when the user is on NewAIAvatarLayout.
  // Match AuthorizedRoute — old_layout === false is enough. Do not require app-theme
  // light/dark; leftover 'blue' from a prior old-layout login must not force blue chrome.
  const isShareableNurturingHistoryPage = location.pathname.startsWith('/url/');
  const useAIAvatarLayout =
    isLoggedIn &&
    serverOldLayout === false &&
    isShareableNurturingHistoryPage;

  if (useAIAvatarLayout) {
    return <Route component={component} {...rest} />;
  }
  
  // If user is logged in, render with sidebar and header
  if (isLoggedIn && user && Object.keys(user).length) {
    const hasAccess = roles
      ? checkRole(normalizeRoles(user?.roles), roles)
      : true;
    
    // Note: FlexibleRoute allows access from all contexts including AI Avatar Layout
    // No restrictions based on user roles for shareable pages
    
    if (!hasAccess) {
      return (
        <>
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
          <Header />
          <Box sx={{display: 'flex', flexDirection: 'row', flex: 1}}>
            <Sidebar />
            <ComponentPageContainer
              style={{ 
                marginLeft: '64px',
                transition: 'margin-left 0.2s ease-in-out'
              }}
            >
              <Route component={NotAuthorizedPage} {...rest} />
            </ComponentPageContainer>
          </Box>
        </Box>
        </>
      );
    }
    
    return (
      <>
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
          <Header />
          <Box sx={{display: 'flex', flexDirection: 'row', width: '100%'}}>
            <Sidebar />
            <ComponentPageContainer
              style={{ 
                marginLeft: '64px',
                transition: 'margin-left 0.2s ease-in-out',
                width: 'calc(100% - 64px)'
              }}
            >
              <Route component={component} {...rest} />
            </ComponentPageContainer>
          </Box>
        </Box>
      </>
    );
  }
  
  // If user is not logged in, render without sidebar and header (like UnAuthorizedRoute)
  return <Route component={component} {...rest} />;
};

export const MyautoiqRoute = ({ component, ...rest }: any) => {
  let pathName = window.location.pathname.split('/')[1];

  if (pathName === 'myautoiq') {
    return (
      <>
        <Route component={component} {...rest} />
      </>
    );
  }
  return <Redirect push to={'/login?redirect=' + window.location.pathname} />;
};
